import { RTCEvent, type RTCAction } from 'rtc-protocol';
import type {
  ConsumerParameters,
  CreateRoomOptions,
  MediaKind,
  MeetingType,
  ProducerInfo,
  RoomInfo,
  TransportDirection,
  TransportParameters
} from '../types/index.js';
import { SocketClient } from './SocketClient.js';

/** Typed facade for every action currently handled by rtc-server/RTCController. */
export class SignalClient {
  constructor(private readonly socket: SocketClient) {}

  request<T>(action: RTCAction, data: Record<string, unknown> = {}): Promise<T> {
    return this.socket.request<T>({ action, data });
  }

  createRoom(options: Omit<CreateRoomOptions, 'media'> = {}): Promise<RoomInfo> {
    return this.request(RTCEvent.CREATE_ROOM, options);
  }

  join(roomId: string): Promise<RoomInfo> {
    return this.request(RTCEvent.JOIN, { roomId });
  }

  leave(roomId: string): Promise<{ roomId: string; left: boolean }> {
    return this.request(RTCEvent.LEAVE, { roomId });
  }

  destroy(roomId: string): Promise<{ roomId: string; destroyed: boolean }> {
    return this.request(RTCEvent.DESTROY, { roomId });
  }

  getRoomInfo(roomId: string): Promise<RoomInfo> {
    return this.request(RTCEvent.INFO, { roomId });
  }

  getRoomMembers(roomId: string): Promise<string[]> {
    return this.request(RTCEvent.MEMBERS, { roomId });
  }

  listRooms(): Promise<RoomInfo[]> {
    return this.request(RTCEvent.LIST_ROOMS);
  }

  getRouterRtpCapabilities(roomId: string): Promise<unknown> {
    return this.request(RTCEvent.GET_ROUTER_RTP_CAPABILITIES, { roomId });
  }

  createWebRtcTransport(roomId: string, direction: TransportDirection): Promise<TransportParameters> {
    return this.request(RTCEvent.CREATE_WEBRTC_TRANSPORT, { roomId, direction });
  }

  connectTransport(roomId: string, transportId: string, dtlsParameters: unknown): Promise<{ connected: boolean }> {
    return this.request(RTCEvent.CONNECT_TRANSPORT, { roomId, transportId, dtlsParameters });
  }

  produce(
    roomId: string,
    transportId: string,
    kind: MediaKind,
    rtpParameters: unknown
  ): Promise<{ id: string }> {
    return this.request(RTCEvent.PRODUCE, { roomId, transportId, kind, rtpParameters });
  }

  consume(
    roomId: string,
    transportId: string,
    producerId: string,
    rtpCapabilities: unknown
  ): Promise<ConsumerParameters> {
    return this.request(RTCEvent.CONSUME, { roomId, transportId, producerId, rtpCapabilities });
  }

  resumeConsumer(roomId: string, consumerId: string): Promise<{ resumed: boolean }> {
    return this.request(RTCEvent.RESUME_CONSUMER, { roomId, consumerId });
  }

  closeProducer(roomId: string, producerId: string): Promise<{ closed: boolean }> {
    return this.request(RTCEvent.CLOSE_PRODUCER, { roomId, producerId });
  }

  getProducers(roomId: string): Promise<ProducerInfo[]> {
    return this.request(RTCEvent.GET_PRODUCERS, { roomId });
  }

  invite(targetId: string, type: MeetingType, roomId?: string): Promise<{ callId: string }> {
    return this.request(RTCEvent.INVITE, { targetId, type, ...(roomId ? { roomId } : {}) });
  }

  accept(callId: string): Promise<{ callId: string; accepted: boolean }> {
    return this.request(RTCEvent.ACCEPT, { callId });
  }

  reject(callId: string): Promise<{ callId: string; rejected: boolean }> {
    return this.request(RTCEvent.REJECT, { callId });
  }

  end(callId: string): Promise<{ callId: string; ended: boolean }> {
    return this.request(RTCEvent.END, { callId });
  }

  setMuted(roomId: string, type: MediaKind, muted: boolean): Promise<Record<string, boolean>> {
    return this.request(muted ? RTCEvent.MUTE : RTCEvent.UNMUTE, { roomId, type });
  }
}
